/**
 * A plain text editor: open a file, read it, change it, find in it, save it. Nothing more — it is
 * for looking at a file an agent touched, not a place to write code.
 *
 * The same page in a browser and in the desktop app; lib/files.ts decides how a file is reached.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from 'react'
import { ChevronDown, ChevronUp, FolderOpen, Save, Search, X } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { canSaveInPlace, FileProblem, openFile, readText, saveFile, saveFileAs, type OpenFile } from '../lib/files'
import { findMatches, lineOf, nextMatch } from '../lib/findInText'
import { EmptyState, ErrorBanner } from '../components/primitives'
import { Explorer, type OpenAs } from '../components/Explorer'
import { isMarkdown } from '../lib/markdownFile'
import { Markdown } from '../components/Markdown'
import { EDITOR_COMMAND_EVENT, takeEditorCommand, type EditorCommand } from '../lib/menuCommands'

export default function Editor() {
  const { t } = useI18n()
  const [file, setFile] = useState<OpenFile | null>(null)
  const [text, setText] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  // A Markdown file from the Explorer opens in the reader; "Edit" switches to the text.
  const [reading, setReading] = useState(false)

  const [findOpen, setFindOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [caseSensitive, setCaseSensitive] = useState(false)
  const [current, setCurrent] = useState(-1)

  const area = useRef<HTMLTextAreaElement>(null)
  const gutter = useRef<HTMLDivElement>(null)
  const findInput = useRef<HTMLInputElement>(null)

  const dirty = file !== null && text !== file.content
  const lines = useMemo(() => text.split('\n').length, [text])
  const matches = useMemo(() => findMatches(text, query, caseSensitive), [text, query, caseSensitive])

  // Leaving with unsaved changes asks first, as any editor does.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const report = useCallback(
    (error: unknown) => setProblem(t(`editor.error.${error instanceof FileProblem ? error.reason : 'failed'}`)),
    [t],
  )

  const discardOk = useCallback(() => !dirty || window.confirm(t('editor.discard')), [dirty, t])

  const load = useCallback((opened: OpenFile, as: OpenAs = 'edit') => {
    setReading(as === 'read' && isMarkdown(opened.name))
    setFile(opened)
    setText(opened.content)
    setProblem(null)
    setCurrent(-1)
    requestAnimationFrame(() => area.current?.focus())
  }, [])

  const open = useCallback(async () => {
    if (!discardOk()) return
    setBusy(true)
    try {
      const opened = await openFile()
      if (opened) load(opened)
    } catch (error) {
      report(error)
    } finally {
      setBusy(false)
    }
  }, [discardOk, load, report])

  const save = useCallback(
    async (as: boolean) => {
      if (!file) return
      setBusy(true)
      try {
        const saved = as ? await saveFileAs(file, text) : await saveFile(file, text)
        if (saved) {
          setFile(saved)
          setProblem(null)
        }
      } catch (error) {
        report(error)
      } finally {
        setBusy(false)
      }
    },
    [file, text, report],
  )

  /** Selects a match in the text and scrolls it into view. */
  const goTo = useCallback(
    (index: number) => {
      const textarea = area.current
      const match = matches[index]
      if (!textarea || !match) return
      const [start, end] = match
      setCurrent(index)
      textarea.focus({ preventScroll: true })
      textarea.setSelectionRange(start, end)
      const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 20
      textarea.scrollTop = Math.max(0, (lineOf(text, start) - 1) * lineHeight - textarea.clientHeight / 3)
      findInput.current?.focus({ preventScroll: true })
    },
    [matches, text],
  )

  const step = useCallback(
    (backwards: boolean) => {
      const textarea = area.current
      const caret = current >= 0 && matches[current] ? matches[current][0] + (backwards ? 0 : 1) : (textarea?.selectionStart ?? 0)
      goTo(nextMatch(matches, caret, backwards))
    },
    [current, matches, goTo],
  )

  const showFind = useCallback(() => {
    setFindOpen(true)
    const selected = area.current ? text.slice(area.current.selectionStart, area.current.selectionEnd) : ''
    if (selected && !selected.includes('\n')) setQuery(selected)
    requestAnimationFrame(() => findInput.current?.select())
  }, [text])

  const closeFind = useCallback(() => {
    setFindOpen(false)
    setCurrent(-1)
    area.current?.focus()
  }, [])

  // The menu bar's Arquivo › Abrir/Salvar/Salvar como and Editar › Localizar (docs/refactor/46): the same as the
  // shortcuts, from anywhere — and Abrir arquivo asked on another screen, done once this one is open.
  useEffect(() => {
    const run = (command: EditorCommand | null) => {
      if (command === 'open') void open()
      else if (command === 'save') void save(false)
      else if (command === 'saveAs') void save(true)
      else if (command === 'find') showFind()
    }
    run(takeEditorCommand())
    const onCommand = () => run(takeEditorCommand())
    window.addEventListener(EDITOR_COMMAND_EVENT, onCommand)
    return () => window.removeEventListener(EDITOR_COMMAND_EVENT, onCommand)
  }, [open, save, showFind])

  // The usual shortcuts, wherever the focus is on this page.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const mod = event.ctrlKey || event.metaKey
    const key = event.key.toLowerCase()
    if (mod && key === 's') {
      event.preventDefault()
      void save(event.shiftKey)
    } else if (mod && key === 'o') {
      event.preventDefault()
      void open()
    } else if (mod && key === 'f') {
      event.preventDefault()
      showFind()
    } else if (event.key === 'F3') {
      event.preventDefault()
      step(event.shiftKey)
    } else if (event.key === 'Escape' && findOpen) {
      event.preventDefault()
      closeFind()
    }
  }

  // A file dropped anywhere on the page opens it. It cannot be saved in place afterwards — the
  // drop gives its content, not a way back — so "save" then asks where.
  const onDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    const dropped = event.dataTransfer.files[0]
    if (!dropped || !discardOk()) return
    try {
      load({ name: dropped.name, content: await readText(dropped) })
    } catch (error) {
      report(error)
    }
  }

  return (
    <div className="editor-layout">
    <Explorer
      onOpen={(opened, as) => {
        if (!discardOk()) return
        load(opened, as)
      }}
    />
    <div
      className={`editor${dragging ? ' editor--dragging' : ''}`}
      onKeyDown={onKeyDown}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes('Files')) {
          event.preventDefault()
          setDragging(true)
        }
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) setDragging(false)
      }}
      onDrop={(event) => void onDrop(event)}
    >
      <div className="editor__toolbar">
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => void open()} disabled={busy} title={`${t('editor.open')} (Ctrl+O)`}>
          <FolderOpen size={14} /> {t('editor.open')}
        </button>
        <button type="button" className="btn btn--sm btn--primary" onClick={() => void save(false)} disabled={busy || !file || (!dirty && canSaveInPlace(file))} title={`${t('common.save')} (Ctrl+S)`}>
          <Save size={14} /> {t('common.save')}
        </button>
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => void save(true)} disabled={busy || !file} title={`${t('editor.saveAs')} (Ctrl+Shift+S)`}>
          {t('editor.saveAs')}
        </button>
        <button type="button" className="btn btn--sm btn--ghost" onClick={showFind} disabled={!file} title={`${t('editor.find')} (Ctrl+F)`}>
          <Search size={14} /> {t('editor.find')}
        </button>

        {file && isMarkdown(file.name) && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setReading(!reading)}>
            {reading ? t('explorer.edit') : t('explorer.read')}
          </button>
        )}

        <div className="spacer" />

        {file && (
          <span className="small editor__name" title={file.path ?? file.name}>
            {file.name}
            {dirty && <span className="editor__dirty" aria-label={t('editor.unsaved')} title={t('editor.unsaved')}> ●</span>}
          </span>
        )}
      </div>

      {findOpen && (
        <div className="editor__find" role="search">
          <input
            ref={findInput}
            className="input"
            value={query}
            placeholder={t('editor.findPlaceholder')}
            aria-label={t('editor.find')}
            onChange={(event) => {
              setQuery(event.target.value)
              setCurrent(-1)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                step(event.shiftKey)
              }
            }}
          />
          <span className="tiny muted editor__count">
            {query ? t('editor.matches', { current: current >= 0 ? current + 1 : 0, count: matches.length }) : ''}
          </span>
          <label className="tiny row row--tight">
            <input type="checkbox" checked={caseSensitive} onChange={(event) => setCaseSensitive(event.target.checked)} />
            {t('editor.caseSensitive')}
          </label>
          <button type="button" className="btn btn--ghost btn--icon" onClick={() => step(true)} disabled={!matches.length} aria-label={t('editor.previous')} title={`${t('editor.previous')} (Shift+Enter)`}>
            <ChevronUp size={15} />
          </button>
          <button type="button" className="btn btn--ghost btn--icon" onClick={() => step(false)} disabled={!matches.length} aria-label={t('editor.next')} title={`${t('editor.next')} (Enter)`}>
            <ChevronDown size={15} />
          </button>
          <button type="button" className="btn btn--ghost btn--icon" onClick={closeFind} aria-label={t('common.close')} title={`${t('common.close')} (Esc)`}>
            <X size={15} />
          </button>
        </div>
      )}

      {problem && <ErrorBanner message={problem} />}

      {file && reading ? (
        <div className="editor__reader">
          <Markdown source={text} />
        </div>
      ) : file ? (
        <div className="editor__body">
          <div className="editor__gutter mono" ref={gutter} aria-hidden="true">
            {Array.from({ length: lines }, (_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>
          <textarea
            ref={area}
            className="editor__text mono"
            value={text}
            spellCheck={false}
            wrap="off"
            aria-label={file.name}
            onChange={(event) => setText(event.target.value)}
            onScroll={(event) => {
              if (gutter.current) gutter.current.scrollTop = event.currentTarget.scrollTop
            }}
          />
        </div>
      ) : (
        <div className="editor__empty">
          <EmptyState
            title={t('editor.emptyTitle')}
            body={t('editor.emptyBody')}
            action={
              <button type="button" className="btn btn--primary" onClick={() => void open()} disabled={busy}>
                <FolderOpen size={15} /> {t('editor.open')}
              </button>
            }
          />
        </div>
      )}
    </div>
    </div>
  )
}
